import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import AuthForm from '@/components/AuthForm';
import { CircuitBoard } from 'lucide-react';
import { useEffect, useState } from 'react';
import BonusPromoCard from '@/components/BonusPromoCard';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const Auth = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [earlyAccessEmail, setEarlyAccessEmail] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  // Debug: Log environment variables (without exposing secrets)
  useEffect(() => {
    console.log('Auth page loaded');
    console.log('Environment variables available:', {
      hasSupabaseUrl: !!import.meta.env.VITE_SUPABASE_URL, 
      hasSupabaseKey: !!import.meta.env.VITE_SUPABASE_ANON_KEY
    });

    // Check if user comes from OAuth redirect
    const hash = window.location.hash;
    if (hash) {
      console.log('Detected hash in URL, possible OAuth redirect');
      
      // This helps debug OAuth issues
      const hashParams = new URLSearchParams(hash.substring(1));
      const errorDescription = hashParams.get('error_description');
      if (errorDescription) {
        setAuthError(errorDescription);
        toast.error(`Auth error: ${errorDescription}`);
      } else {
        console.log('Processing auth callback');
        toast.info('Finalizing authentication...');
      }
    }
    
    // Process OAuth response
    const { data, error } = supabase.auth.getSession();
    console.log('Current auth session:', data?.session ? 'Found' : 'None');
    if (error) {
      console.error('Auth session error:', error);
      setAuthError(error.message);
    }
  }, []);

  useEffect(() => {
    if (user && !isLoading) {
      console.log('User authenticated, redirecting to dashboard');
      navigate('/dashboard');
    } else if (!isLoading) {
      console.log('No authenticated user found');
    }
    
    // Check if there's an email stored from the early access registration
    const storedEmail = localStorage.getItem('earlyAccessEmail');
    if (storedEmail) {
      setEarlyAccessEmail(storedEmail);
      // Clear it after retrieval
      localStorage.removeItem('earlyAccessEmail');
    }
  }, [user, isLoading, navigate]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 right-1/4 w-96 h-96 bg-[#9E7C47] rounded-full filter blur-[120px] animate-pulse-slow"></div>
        <div className="absolute bottom-20 left-1/4 w-96 h-96 bg-[#F9F1A9] rounded-full filter blur-[120px] animate-pulse-slow"></div>
      </div>
      
      <div className="w-full max-w-md text-center relative z-10">
        <div className="mb-8 flex flex-col items-center">
          <img src="/coin_logo.png" alt="AI Wealth Manager" className="h-24 w-24 mb-4" />
          <h1 className="text-3xl font-bold mb-2 text-white">AI Wealth Manager</h1>
          <p className="text-white/70">Create an account or sign in to access your wealth dashboard</p>
          
          {authError && (
            <div className="mt-4 p-3 bg-red-500/20 text-red-200 rounded-lg text-sm">
              Authentication Error: {authError}
            </div>
          )}
        </div>
        
        <BonusPromoCard />
        
        <AuthForm initialEmail={earlyAccessEmail} />
        
        <div className="mt-8">
          <a href="/" className="text-white/50 hover:text-white text-sm">
            ← Back to home
          </a>
        </div>
      </div>
    </div>
  );
};

export default Auth;
